import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BarChart3, CircleCheck, CirclePause, Layers, Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FeatureSwitch from '../../components/FeatureSwitch'
import useFeatureToggle from '../../components/useFeatureToggle'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import StatStrip from '../../components/StatStrip'
import { useToast } from '../../components/toastContext'
import { dateColumn, flagColumn, indexColumn, statusColumn } from './cells'
import SurveyReportDrawer from './SurveyReportDrawer'
import {
  deleteSurvey,
  getSurveysPage,
  listSurveys,
  setSurveysEnabled,
} from './engagementService'
import { engagementStats as ST, surveysStrings as S } from './strings'
import './engagement.css'
import { serverText } from '../../lib/i18n'

/**
 * إدارة الاستبيانات — Views/SurveysManagement/Index.cshtml.
 *
 * The list defaults to `إظهار الاستبيانات النشطة فقط`, which is the option the
 * legacy select marks `selected`.
 */
/* `/surveys-management`, NOT `/surveys`. `/Surveys` is the LEARNER's own screen — the two
   were split when this admin list took a name of its own (see `legacyRoutes.js`). Navigating
   to the short one leaves the admin app entirely: the browser loads the learner portal,
   which bounces through the login page to the dashboard. */
export default function SurveysPage() {
  const navigate = useNavigate()
  const toast = useToast()
  /* OFF on arrival. It opened filtered to active surveys, with a chip already in
     the bar saying so — an inactive survey simply was not in the list until you
     noticed the chip and removed it. The filtering is client-side (see
     `listSurveys`), so the unfiltered list costs nothing extra. */
  const [activeOnly, setActiveOnly] = useRemembered('surveys:activeOnly', false)
  const [featureOn, setFeatureOn] = useState(true)
  const [query, setQuery] = useRemembered('surveys:query', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [reporting, setReporting] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  /* Remembered like the search above — the scope card decides WHICH surveys those page
     numbers count, and Back used to return to page 2 of the unfiltered list. */
  const [scope, setScope] = useRemembered('surveys:scope', null)

  useEffect(() => {
    getSurveysPage()
      .then((page) => setFeatureOn(page.enabled))
      .catch(() => setFeatureOn(false))
  }, [])

  /* Shared with every other activation switch — see useFeatureToggle. */
  const { toggle: toggleFeature } = useFeatureToggle({
    setValue: setFeatureOn,
    save: setSurveysEnabled,
  })

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listSurveys({ activeOnly })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [activeOnly, reloadKey, toast])

  /* Counted off the rows already on the page; the two state cards narrow the
     list to what they count. */
  const stats = useMemo(() => {
    const active = rows.filter((row) => row.isActive).length
    return { total: rows.length, active, inactive: rows.length - active }
  }, [rows])

  /* Both narrowings, in one place: the state card and the active-only chip. */
  const showingEverything = !scope && !activeOnly

  const visibleRows = useMemo(() => {
    if (scope === 'active') return rows.filter((row) => row.isActive)
    if (scope === 'inactive') return rows.filter((row) => !row.isActive)
    return rows
  }, [rows, scope])

  const columns = useMemo(
    () => [
      indexColumn(S.columns.index),
      {
        key: 'title',
        header: S.columns.title,
        render: (row) => (
          <bdi className="eng__title" title={row.title}>
            {row.title}
          </bdi>
        ),
      },
      {
        key: 'type',
        header: S.columns.type,
        value: (row) => S.types[row.type] ?? '',
        render: (row) => <bdi>{S.types[row.type] ?? ''}</bdi>,
      },
      dateColumn('createdOn', S.columns.createdOn),
      dateColumn('updatedOn', S.columns.updatedOn),
      statusColumn('isActive', S.columns.status, S),
      flagColumn('mandatory', S.columns.mandatory, S),
      /* `إجمالي الإجابات` REMOVED. The payload carries `QuestionsCount` (how many questions
         the survey asks) and a `HasAnswers` boolean — nothing that is a count of answers —
         so the column was blank on every row, on every survey, and always would have been.
         An empty column reads as "no answers yet", which is a statement the data cannot
         support. The report drawer is where response figures live. */
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              {
                id: 'details',
                icon: BarChart3,
                label: S.row.details,
                tone: 'info',
                onClick: () => setReporting(row),
              },
              { id: 'update', icon: Pencil, label: S.row.update, onClick: () => navigate(`/surveys-management/${row.id}/edit`) },
              /* `إرسال بريد` IS NOT HERE. Nothing in this slice sends one — the
                 legacy's send action has no counterpart in the port, and a mail button
                 that opens nothing is worse than its absence. COSMETIC_UI.md. */
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [navigate],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        count={loading ? undefined : rows.length}
        actions={
          <>
            {/* The legacy screen carries this switch (LocChkBxEnable); it was the one
                control the clone left unrendered. It is a setting, so it sits in the
                header and never among the filters. */}
            <FeatureSwitch checked={featureOn} onChange={toggleFeature} label={S.enableFeature} />
            <Button onClick={() => navigate('/surveys-management/new')}>
              <Plus size={16} strokeWidth={2} />
              {S.create}
            </Button>
          </>
        }
      />

      <StatStrip
        label={ST.caption}
        /* Lit only while the list really is every survey. `activeOnly` narrows it from
           outside this strip, so with that chip on nothing is lit and the chip is the
           thing explaining why. */
        activeId={showingEverything ? 'all' : scope}
        onSelect={(id) => {
          /* ALL IS NOT A FILTER TO TOGGLE, it is the absence of one - selecting it clears
             the state scope AND the active-only chip. A card that says "all surveys" while
             that chip still hides the disabled ones would be naming a list it is not
             showing. */
          if (id === 'all') {
            setScope(null)
            setActiveOnly(false)
            return
          }
          setScope((current) => (current === id ? null : id))
        }}
        items={[
          {
            /* Was a `selectable: false` figure labelled "Total" - the one card in the strip
               that could not be clicked, and the only way back to the full list was to
               click the selected card a second time. */
            id: 'all',
            icon: Layers,
            label: S.allSurveys,
            value: stats.total,
            tone: 'violet',
          },
          {
            id: 'active',
            icon: CircleCheck,
            label: ST.active,
            value: stats.active,
            tone: 'success',
          },
          {
            id: 'inactive',
            icon: CirclePause,
            label: ST.inactive,
            value: stats.inactive,
            tone: 'warning',
          },
        ]}
      />

      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: S.columns.title }}
        chips={
          activeOnly
            ? [
                {
                  id: 'activeOnly',
                  label: S.columns.status,
                  value: S.activeOnly,
                  onRemove: () => setActiveOnly(false),
                },
              ]
            : []
        }
      />

      <DataTable
        stateKey="surveys"
        columns={columns}
        rows={visibleRows}
        loading={loading}
        query={query}
        /* Both filters sit outside the table, which cannot see either — narrowing to the
           active surveys kept a page the shorter list does not have. */
        resetKey={`${scope}:${activeOnly}`}
        controls={false}
        getRowId={(row) => row.id}
        initialSort={{ key: 'createdOn', dir: 'desc' }}
      />


      <SurveyReportDrawer
        open={!!reporting}
        survey={reporting}
        onClose={() => setReporting(null)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await deleteSurvey(pendingDelete.id)
          if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
          setPendingDelete(null)
          toast.success(serverText(outcome.message) || S.deleted)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
